import { useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { endpoints } from "../api.js";
import { LOCALE_KEY, writeStorage } from "../lib/storage.js";
import { DEFAULT_LOCALE } from "./locales.js";

export function useLocale() {
  const { locale } = useParams();
  return locale || DEFAULT_LOCALE;
}

/** Prefix an app path with the active locale: lp("/settings") -> "/en/settings". */
export function useLocalePath() {
  const locale = useLocale();
  return useCallback((path = "") => `/${locale}${path.startsWith("/") || !path ? path : `/${path}`}`, [locale]);
}

/** Switch locale: rewrite the URL prefix, remember it locally and on the profile. */
export function useChangeLocale({ saveToProfile = true } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(
    (code) => {
      writeStorage(LOCALE_KEY, code);
      const parts = location.pathname.split("/");
      parts[1] = code;
      navigate(`${parts.join("/")}${location.search}${location.hash}`);
      if (saveToProfile) endpoints.patchMe({ locale: code }).catch(() => {});
    },
    [navigate, location, saveToProfile],
  );
}

/** Message for an ApiError: a localized `error.<code>` if we have one, else the server's text. */
export function useErrorMessage() {
  const intl = useIntl();
  return useCallback(
    (err) => {
      if (!err) return "";
      const id = `error.${err.code}`;
      if (err.code && Object.prototype.hasOwnProperty.call(intl.messages, id)) {
        return intl.formatMessage({ id });
      }
      if (err.message) return err.message;
      return intl.formatMessage({ id: "error.generic" });
    },
    [intl],
  );
}

export function useDocumentTitle(id, values) {
  const intl = useIntl();
  const title = id ? intl.formatMessage({ id }, values) : "";
  const brand = intl.formatMessage({ id: "brand.name" });
  useEffect(() => {
    document.title = title ? `${title} · ${brand}` : brand;
  }, [title, brand]);
}
